// ENG-7712: the scheduled-turn delivery marker - shared contract (writer side).
//
// Stops a scheduled task's self-emitted outcome from riding along in the agent's
// currently-active Slack thread. Since ENG-6849 a scheduled task is injected as a
// plain user turn into the agent's live session with NO Slack inbound coordinate,
// so a self-emitted slack.reply inherits whatever thread the agent last posted in
// and the outcome lands in an unrelated live conversation.
//
// The manager (apps/cli, which imports this) stamps this marker - a JSON file in
// the agent home dir - when it injects a scheduled-task turn. The slack channel
// MCP reads it and forces the send to the task's own destination (or a fresh
// top-level post), never an ambient thread.
//
// CONTRACT: packages/mcp has no @augmented/core dependency (see the
// agentSlashCommand note in packages/mcp/src/slack-channel.ts), so the reader
// keeps its own copy in packages/mcp/src/scheduled-turn-marker.ts. This file is
// the writer's canonical FILENAME + shape; the MCP mirror also holds the
// freshness window + the validate/resolve logic that only the reader runs, so
// the two files are NOT byte-identical and are not meant to be. Only the wire
// surface is shared: keep the FILENAME and the marker JSON shape in sync across
// the two. ENG-8143 pins that shared half with a parity test in apps/cli, the
// one package that can import both sides.

/** The marker filename, resolved inside the agent home dir on both sides. */
export const SCHEDULED_TURN_MARKER_FILENAME = '.current-scheduled-turn.json';

/** The task's own Slack destination, when it has one (derived from delivery_to). */
export interface ScheduledTurnTarget {
  /** Slack channel id (C…/G…/D…) the outcome must go to. */
  channel_id: string;
  /** The task's OWN origin/delivery thread, when it captured one (ENG-6038). */
  thread_ts?: string;
}

/** The on-disk marker the manager stamps for a scheduled-task turn. */
export interface ScheduledTurnMarker {
  /** Epoch ms when the turn was injected - drives the reader's freshness window. */
  ts: number;
  /** The scheduled task id, for observability. */
  task_id?: string;
  /** The task's resolved Slack destination; absent ⇒ the reader posts top-level. */
  target?: ScheduledTurnTarget;
}
